import {
  Article as ArticleIcon,
  Assessment as AssessmentIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Code as CodeIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Movie as MovieIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useAttachmentContext } from "../../context/AttachmentContext";

// Helper to get icon based on file type
const getFileIcon = (mimeType: string) => {
  switch (mimeType) {
    case "application/pdf":
      return <PictureAsPdfIcon />;
    case "application/json":
    case "binary/octet-stream":
      return <CodeIcon />;
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return <ArticleIcon />;
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return <AssessmentIcon />;
    default:
      if (mimeType.startsWith("image/")) {
        return <ImageIcon />;
      }
      if (mimeType.startsWith("video/")) {
        return <MovieIcon />;
      }
      return <InsertDriveFileIcon />;
  }
};

interface IFilePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  attachmentIndex: number;
  fullscreenContainer: HTMLElement | null;
}

const FilePreviewDialog: React.FC<IFilePreviewDialogProps> = ({
  open,
  onClose,
  attachmentIndex,
  fullscreenContainer,
}) => {
  const { attachments } = useAttachmentContext();
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentAttachmentIndex, setCurrentAttachmentIndex] =
    useState(attachmentIndex);

  useEffect(() => {
    setCurrentAttachmentIndex(attachmentIndex);
  }, [attachmentIndex]);

  const currentAttachment = useMemo(
    () => attachments[currentAttachmentIndex],
    [attachments, currentAttachmentIndex]
  );

  const handleNext = () => {
    setFileContent(null);
    setCurrentAttachmentIndex((prevIndex) =>
      Math.min(prevIndex + 1, attachments.length - 1)
    );
  };

  const handlePrevious = () => {
    setFileContent(null);
    setCurrentAttachmentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const fetchFileContent = async (url: string) => {
    try {
      setIsLoading(true);
      const response = await axios.get(url, {
        responseType: "text",
      });

      const contentType = response.headers["content-type"];
      if (
        response.status === 200 &&
        ((contentType && typeof contentType === "string" && contentType.startsWith("text")) || response.data)
      ) {
        setFileContent(response.data);
      } else {
        setFileContent("Unable to load file content");
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      console.error("Error fetching file content:", error);
      setFileContent("Error loading the file content.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRedirectUrl = async (contentUrl: string) => {
    try {
      const jiraAccessToken = localStorage.getItem("jiraAccessToken") || "";
      const access_token = jiraAccessToken.replace(/"/g, "");
      // API call to fetch redirect URL for the attachment
      const response = await fetch(contentUrl, {
        method: "get",
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      if (
        response.url &&
        (mimeType === "application/json" ||
          mimeType === "binary/octet-stream" ||
          mimeType.startsWith("text/"))
      ) {
        await fetchFileContent(response.url);
      }

      return response.redirected ? response.url : "";
    } catch (error) {
      console.error("Error fetching redirect URL:", error);
      return null;
    }
  };

  const isFirstAttachment = currentAttachmentIndex === 0;
  const isLastAttachment = currentAttachmentIndex === attachments.length - 1;

  const { mimeType, filename, content, thumbnail } = currentAttachment ?? {};

  useEffect(() => {
    const fetchUrl = async () => {
      if (content) {
        setIsLoading(true);
        const url = await fetchRedirectUrl(content);
        setRedirectUrl(url);
        setIsLoading(false);
      }
    };
    fetchUrl();
  }, [content]);

  if (!currentAttachment) {
    return null;
  }

  const renderPreviewContent = () => {
    if (isLoading) {
      return null;
    }
    if (redirectUrl) {
      if (mimeType.startsWith("image/")) {
        return (
          <Box
            component="img"
            src={redirectUrl || thumbnail || content}
            alt={filename}
            sx={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 200px)",
              objectFit: "contain",
              display: "block",
              margin: "auto",
            }}
          />
        );
      } else if (mimeType.startsWith("video/")) {
        return (
          <Box
            component="video"
            controls
            src={redirectUrl || content}
            sx={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 200px)",
              objectFit: "contain",
              display: "block",
              margin: "auto",
              borderRadius: "6px",
            }}
          />
        );
      } else if (
        mimeType === "application/json" ||
        mimeType === "binary/octet-stream" ||
        mimeType.startsWith("text/")
      ) {
        return (
          <Box
            sx={{
              width: "100%",
              height: "calc(100vh - 200px)",
              overflow: "auto",
              backgroundColor: "#282c34", // Dark background for code
              color: "#abb2bf",
              p: 2,
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              borderRadius: "6px",
            }}
          >
            <pre style={{ margin: 2, padding: 0 }}>
              <code style={{ textWrap: "wrap" }}>
                {fileContent ?? "Loading content..."}
              </code>
            </pre>
          </Box>
        );
      } else if (
        mimeType === "application/pdf" ||
        mimeType ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        mimeType ===
          "application/vnd.openxmlformats-officedocument.presentationml.document"
      ) {
        const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
          redirectUrl || content
        )}&embedded=true`;
        return (
          <Box
            component="iframe"
            src={viewerUrl}
            title={filename}
            sx={{
              width: "100%",
              height: "calc(100vh - 200px)",
              border: "none",
            }}
          />
        );
      }
    }
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - 200px)",
          color: "text.secondary",
        }}
      >
        {getFileIcon(mimeType)} {/* Use mimeType directly */}
        <Typography variant="h6" sx={{ mt: 2, textAlign: "center" }}>
          Preview not available.
          <br />
          Please download to view.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          File Name: {filename}
        </Typography>
        <IconButton
          href={redirectUrl || content}
          download={filename}
          aria-label="download file"
          sx={{
            mt: 3,
            p: 2,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            color: "primary.contrastText",
          }}
          title={`Download ${filename}`}
        >
          <DownloadIcon sx={{ fontSize: 40 }} />
        </IconButton>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      container={fullscreenContainer}
      PaperProps={{
        sx: {
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          margin: "2rem",
          borderRadius: "8px",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getFileIcon(mimeType)}
          <Box>
            <Typography variant="subtitle1" component="div">
              {filename}
            </Typography>
          </Box>
        </Box>
        <Box>
          <IconButton
            href={redirectUrl || content}
            download={filename}
            aria-label="download"
            sx={{ mr: 1 }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Main Content Area with Navigation */}
      <DialogContent
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          p: 0,
          px: 2,
        }}
      >
        <IconButton
          onClick={handlePrevious}
          disabled={isFirstAttachment || isLoading}
          sx={{
            backgroundColor: "rgba(0,0,0,0.5)",
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.7)",
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            overflow: "auto",
            p: 2,
          }}
        >
          {isLoading ? <CircularProgress /> : renderPreviewContent()}
        </Box>

        <IconButton
          onClick={handleNext}
          disabled={isLastAttachment || isLoading}
          sx={{
            backgroundColor: "rgba(0,0,0,0.5)",
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.7)",
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewDialog;
